import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getMaintenancePlanV2HousesHouseIdMaintenancePlanGetQueryKey } from '@/client/@tanstack/react-query.gen';

/**
 * How long to wait before reading the plan a second time.
 *
 * No write returns an actionable card. Both write endpoints commit their decision and then ask the
 * plan cache to reconcile, so the read that produces the new card can run after the response. This
 * broad second read protects action writes and creation from hosts without an in-place pending row;
 * the schedule's entry-specific early retries live in `usePendingCustomEntries`.
 */
const PLAN_RECONCILE_GRACE_MS = 2500;

/**
 * Re-read the property's plan after a write.
 *
 * The plan is the one read model, and neither `POST …/actions` nor `POST …/custom-entries` returns
 * the card it affected — an action answers with `{ ok, event_id, action, scope }`, which says a
 * write happened, not what the card now looks like. So there is nothing to patch into the cache
 * from a response and every write ends here.
 *
 * Invalidating by house rather than by filter is deliberate: the schedule holds several plan queries
 * at once (Upcoming, History, whatever pills are selected), and one action can move a card between
 * them — completing something takes it out of Upcoming and puts it into History. Invalidating only
 * the query that was on screen would leave the others stale. The generated key nests its options in
 * one object, so a key built with just the path matches every filter variant for that house.
 *
 * `scope` on the response ('occurrence' | 'obligation') is not consulted: both live in the same
 * read, so both are covered by the same invalidation, and branching on it would only add a way to
 * get it wrong.
 */
export const usePlanRefresh = (houseId: number | undefined) => {
	const invalidatePlan = usePlanInvalidation(houseId);
	const delayedInvalidations = useRef(new Map<number | undefined, number>());

	return useCallback(() => {
		invalidatePlan();

		// A review pass can settle several cards inside this window. Each verdict still marks the plan
		// stale immediately, but only the last one needs to schedule the eventual-consistency read.
		// Deliberately do not clear this on unmount: navigating back to Schedule is when the delayed
		// invalidation matters most.
		const pending = delayedInvalidations.current.get(houseId);
		if (pending !== undefined) window.clearTimeout(pending);
		const timeoutId = window.setTimeout(() => {
			if (delayedInvalidations.current.get(houseId) === timeoutId) {
				delayedInvalidations.current.delete(houseId);
			}
			invalidatePlan();
		}, PLAN_RECONCILE_GRACE_MS);
		delayedInvalidations.current.set(houseId, timeoutId);
	}, [houseId, invalidatePlan]);
};

/** Re-read every active plan lens once, without scheduling another reconciliation wave. */
export const usePlanInvalidation = (houseId: number | undefined) => {
	const queryClient = useQueryClient();

	return useCallback(() => {
		if (houseId === undefined) return Promise.resolve();
		const queryKey = getMaintenancePlanV2HousesHouseIdMaintenancePlanGetQueryKey({
			path: { house_id: houseId },
		});
		return queryClient.invalidateQueries({ queryKey });
	}, [houseId, queryClient]);
};
